// components/CreateTemplateModal.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  FaArrowDown,
  FaArrowUp,
  FaEnvelopeOpenText,
  FaImage,
  FaLink,
  FaPlus,
  FaSave,
  FaSignature,
  FaTimes,
  FaTrash,
} from "react-icons/fa";

import {
  createEmailTemplate,
  updateEmailTemplate,
} from "../../../../../services/mailingServices";

import { PRODUCT_OPTIONS } from "../constants";

const BLOCK_TYPES = [
  { value: "title", label: "Titre" },
  { value: "subtitle", label: "Sous-titre" },
  { value: "paragraph", label: "Paragraphe" },
  { value: "image", label: "Image" },
  { value: "button", label: "Bouton" },
  { value: "divider", label: "Séparateur" },
  { value: "signature", label: "Signature" },
];

const DEFAULT_BLOCKS = [
  {
    type: "title",
    content: "Simplifiez la gestion de {{nom_club}}",
  },
  {
    type: "paragraph",
    content:
      "Bonjour,\n\nJe me permets de vous présenter une solution web conçue pour simplifier le quotidien de votre structure.",
  },
  {
    type: "paragraph",
    content:
      "L’objectif est de vous faire gagner du temps, améliorer votre communication et centraliser vos informations importantes dans un outil simple à utiliser.",
  },
  {
    type: "button",
    label: "Voir la démonstration",
    url: "https://www.monappliclub.com",
  },
  {
    type: "signature",
  },
];

function slugify(value = "") {
  return String(value)
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "")
    .slice(0, 170);
}

function escapeHtml(value = "") {
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function applyPreviewVars(value = "") {
  const vars = {
    nom_club: "Club de démonstration",
    discipline: "Judo",
    departement_code: "33",
    nb_licencies: "120",
    contact_nom: "Madame, Monsieur",
  };

  return String(value).replace(/\{\{(\w+)\}\}/g, (_, key) => vars[key] || "");
}

function blockToPreviewHtml(block) {
  if (!block) return "";

  if (block.type === "title") {
    return `
      <h1 style="font-size:28px;line-height:1.25;margin:0 0 18px;color:#0f172a;">
        ${escapeHtml(applyPreviewVars(block.content))}
      </h1>
    `;
  }

  if (block.type === "subtitle") {
    return `
      <h2 style="font-size:20px;line-height:1.35;margin:24px 0 12px;color:#1e293b;">
        ${escapeHtml(applyPreviewVars(block.content))}
      </h2>
    `;
  }

  if (block.type === "paragraph") {
    return `
      <p style="font-size:15px;line-height:1.7;margin:0 0 15px;color:#334155;">
        ${escapeHtml(applyPreviewVars(block.content)).replace(/\n/g, "<br/>")}
      </p>
    `;
  }

  if (block.type === "image" && block.src) {
    return `
      <div style="margin:24px 0;text-align:center;">
        <img
          src="${escapeHtml(block.src)}"
          alt="${escapeHtml(block.alt || "")}"
          style="display:block;max-width:100%;margin:auto;border-radius:16px;border:1px solid #e2e8f0;"
        />
      </div>
    `;
  }

  if (block.type === "button" && block.url) {
    return `
      <div style="margin:26px 0;text-align:center;">
        <a
          href="${escapeHtml(block.url)}"
          style="display:inline-block;background:#2563eb;color:#ffffff;text-decoration:none;
          padding:13px 22px;border-radius:999px;font-weight:700;font-size:15px;"
        >
          ${escapeHtml(applyPreviewVars(block.label || "En savoir plus"))}
        </a>
      </div>
    `;
  }

  if (block.type === "divider") {
    return `<hr style="border:none;border-top:1px solid #e2e8f0;margin:26px 0;" />`;
  }

  if (block.type === "signature") {
    return `
      <p style="margin:24px 0 0;font-size:14px;line-height:1.6;color:#334155;">
        <strong>Laurent VICTORIN</strong><br/>
        Développeur Web & Applications — Président / Enseignant Judo<br/>
        06.80.72.77.07<br/>
        <a href="https://www.monappliclub.com" style="color:#2563eb;">monappliclub.com</a>
      </p>
    `;
  }

  return "";
}

function buildPreviewHtml(blocks = [], preheader = "") {
  const body = blocks.map(blockToPreviewHtml).join("");

  return `
    <div style="background:#f1f5f9;padding:24px;font-family:Arial,sans-serif;">
      <div style="display:none;max-height:0;overflow:hidden;color:transparent;">
        ${escapeHtml(preheader || "")}
      </div>

      <div style="max-width:680px;margin:auto;background:#ffffff;border:1px solid #e2e8f0;border-radius:20px;overflow:hidden;">
        <div style="padding:30px;">
          ${body}

          <hr style="border:none;border-top:1px solid #e2e8f0;margin:30px 0;" />

          <p style="font-size:12px;line-height:1.5;color:#64748b;">
            Vous recevez cet email car votre structure peut être concernée par mes solutions web.
            <br/>
            <a href="https://www.monappliclub.com/desinscription" style="color:#64748b;text-decoration:underline;">Se désinscrire</a>
          </p>
        </div>
      </div>
    </div>
  `;
}

function newBlock(type = "paragraph") {
  if (type === "title") {
    return { type: "title", content: "Votre titre ici" };
  }

  if (type === "subtitle") {
    return { type: "subtitle", content: "Votre sous-titre ici" };
  }

  if (type === "paragraph") {
    return {
      type: "paragraph",
      content: "Votre paragraphe ici.",
    };
  }

  if (type === "image") {
    return {
      type: "image",
      src: "",
      alt: "",
    };
  }

  if (type === "button") {
    return {
      type: "button",
      label: "Voir la démonstration",
      url: "https://www.monappliclub.com",
    };
  }

  if (type === "divider") {
    return { type: "divider" };
  }

  if (type === "signature") {
    return { type: "signature" };
  }

  return {
    type: "paragraph",
    content: "Votre texte ici.",
  };
}

function parseDesignJson(value) {
  if (!value) return DEFAULT_BLOCKS;

  if (Array.isArray(value)) return value;

  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : DEFAULT_BLOCKS;
  } catch {
    return DEFAULT_BLOCKS;
  }
}

export default function CreateTemplateModal({
  initial = null,
  onClose,
  onSaved,
}) {
  const isEdit = !!initial?.id;

  const [saving, setSaving] = useState(false);
  const [slugTouched, setSlugTouched] = useState(false);

  const [form, setForm] = useState({
    name: "",
    slug: "",
    product_key: "monappliclub",
    subject: "",
    preheader: "",
    status: "active",
    design_json: DEFAULT_BLOCKS,
  });

  const previewHtml = useMemo(
    () => buildPreviewHtml(form.design_json, form.preheader),
    [form.design_json, form.preheader],
  );

  useEffect(() => {
    if (initial) {
      setForm({
        name: initial.name || "",
        slug: initial.slug || "",
        product_key: initial.product_key || "monappliclub",
        subject: initial.subject || "",
        preheader: initial.preheader || "",
        status: initial.status || "active",
        design_json: parseDesignJson(initial.design_json),
      });
      setSlugTouched(true);
    } else {
      const defaultName = "Nouveau modèle email";
      setForm({
        name: defaultName,
        slug: slugify(defaultName),
        product_key: "monappliclub",
        subject: "Découvrez une solution simple pour votre structure",
        preheader:
          "Une solution web claire pour gagner du temps et améliorer votre organisation.",
        status: "active",
        design_json: DEFAULT_BLOCKS,
      });
      setSlugTouched(false);
    }
  }, [initial]);

  const updateField = (key, value) => {
    setForm((prev) => {
      const next = {
        ...prev,
        [key]: value,
      };

      if (key === "name" && !slugTouched) {
        next.slug = slugify(value);
      }

      return next;
    });
  };

  const updateBlock = (index, patch) => {
    setForm((prev) => {
      const blocks = [...prev.design_json];
      blocks[index] = {
        ...blocks[index],
        ...patch,
      };

      return {
        ...prev,
        design_json: blocks,
      };
    });
  };

  const addBlock = (type) => {
    setForm((prev) => ({
      ...prev,
      design_json: [...prev.design_json, newBlock(type)],
    }));
  };

  const removeBlock = (index) => {
    setForm((prev) => ({
      ...prev,
      design_json: prev.design_json.filter((_, i) => i !== index),
    }));
  };

  const moveBlock = (index, direction) => {
    setForm((prev) => {
      const nextIndex = index + direction;

      if (nextIndex < 0 || nextIndex >= prev.design_json.length) {
        return prev;
      }

      const blocks = [...prev.design_json];
      const current = blocks[index];

      blocks[index] = blocks[nextIndex];
      blocks[nextIndex] = current;

      return {
        ...prev,
        design_json: blocks,
      };
    });
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      alert("Le nom du modèle est requis.");
      return;
    }

    if (!form.slug.trim()) {
      alert("Le slug du modèle est requis.");
      return;
    }

    if (!form.subject.trim()) {
      alert("Le sujet de l’email est requis.");
      return;
    }

    try {
      setSaving(true);

      const payload = {
        name: form.name.trim(),
        slug: form.slug.trim(),
        product_key: form.product_key,
        subject: form.subject.trim(),
        preheader: form.preheader.trim(),
        status: form.status,
        design_json: form.design_json,
      };

      const saved = isEdit
        ? await updateEmailTemplate(initial.id, payload)
        : await createEmailTemplate(payload);

      if (!saved) {
        throw new Error("Sauvegarde impossible.");
      }

      if (typeof onSaved === "function") {
        onSaved(saved);
      }

      onClose();
    } catch (error) {
      console.error(error);
      alert("Erreur lors de la sauvegarde du modèle.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[999] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/55" onClick={onClose} />

      <div className="relative w-[97vw] max-w-7xl max-h-[94vh] overflow-hidden rounded-3xl bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 shadow-2xl">
        <div className="flex items-start justify-between gap-4 border-b border-slate-200 dark:border-slate-800 p-5">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-200 px-3 py-1 text-xs font-medium">
              <FaEnvelopeOpenText />
              Modèle d’email
            </div>

            <h2 className="mt-3 text-xl font-semibold">
              {isEdit ? "Modifier le modèle" : "Créer un modèle d’email"}
            </h2>

            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              Construisez un email propre avec des blocs réutilisables.
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-2 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
          >
            <FaTimes />
          </button>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-[1fr_520px] max-h-[calc(94vh-108px)] overflow-auto">
          <div className="p-5 space-y-5">
            <section className="rounded-3xl border border-slate-200 dark:border-slate-800 p-4">
              <div className="font-semibold">Informations générales</div>

              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Nom du modèle">
                  <input
                    value={form.name}
                    onChange={(e) => updateField("name", e.target.value)}
                    className="w-full rounded-2xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-4 py-2.5 text-sm"
                  />
                </Field>

                <Field label="Slug">
                  <input
                    value={form.slug}
                    onChange={(e) => {
                      setSlugTouched(true);
                      updateField("slug", slugify(e.target.value));
                    }}
                    className="w-full rounded-2xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-4 py-2.5 text-sm"
                  />
                </Field>

                <Field label="Produit">
                  <select
                    value={form.product_key}
                    onChange={(e) => updateField("product_key", e.target.value)}
                    className="w-full rounded-2xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-4 py-2.5 text-sm"
                  >
                    {PRODUCT_OPTIONS.map((product) => (
                      <option key={product.value} value={product.value}>
                        {product.label}
                      </option>
                    ))}
                  </select>
                </Field>

                <Field label="Statut">
                  <select
                    value={form.status}
                    onChange={(e) => updateField("status", e.target.value)}
                    className="w-full rounded-2xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-4 py-2.5 text-sm"
                  >
                    <option value="draft">Brouillon</option>
                    <option value="active">Actif</option>
                    <option value="archived">Archivé</option>
                  </select>
                </Field>

                <Field label="Sujet de l’email" className="md:col-span-2">
                  <input
                    value={form.subject}
                    onChange={(e) => updateField("subject", e.target.value)}
                    placeholder="Ex : MonAppliClub : simplifiez la gestion de votre club"
                    className="w-full rounded-2xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-4 py-2.5 text-sm"
                  />
                </Field>

                <Field label="Préheader" className="md:col-span-2">
                  <input
                    value={form.preheader}
                    onChange={(e) => updateField("preheader", e.target.value)}
                    placeholder="Petit texte d’aperçu affiché dans certaines boîtes mail"
                    className="w-full rounded-2xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-4 py-2.5 text-sm"
                  />
                </Field>
              </div>
            </section>

            <section className="rounded-3xl border border-slate-200 dark:border-slate-800 p-4">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div>
                  <div className="font-semibold">Blocs de contenu</div>
                  <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                    Variables disponibles : <code>{"{{nom_club}}"}</code>,{" "}
                    <code>{"{{discipline}}"}</code>,{" "}
                    <code>{"{{departement_code}}"}</code>,{" "}
                    <code>{"{{nb_licencies}}"}</code>.
                  </p>
                </div>

                <select
                  onChange={(e) => {
                    if (!e.target.value) return;
                    addBlock(e.target.value);
                    e.target.value = "";
                  }}
                  className="rounded-2xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-4 py-2.5 text-sm"
                >
                  <option value="">+ Ajouter un bloc</option>
                  {BLOCK_TYPES.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="mt-5 space-y-3">
                {form.design_json.map((block, index) => (
                  <BlockEditor
                    key={`${block.type}-${index}`}
                    block={block}
                    index={index}
                    onChange={(patch) => updateBlock(index, patch)}
                    onRemove={() => removeBlock(index)}
                    onMoveUp={() => moveBlock(index, -1)}
                    onMoveDown={() => moveBlock(index, 1)}
                    canMoveUp={index > 0}
                    canMoveDown={index < form.design_json.length - 1}
                  />
                ))}
              </div>
            </section>
          </div>

          <aside className="border-t xl:border-t-0 xl:border-l border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/60 p-5">
            <div className="sticky top-0 space-y-4">
              <div className="rounded-3xl bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 p-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="font-semibold">Prévisualisation</div>
                    <div className="mt-1 text-xs text-slate-500">
                      Exemple avec un prospect fictif.
                    </div>
                  </div>
                </div>

                <div className="mt-3 rounded-2xl bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-3">
                  <div className="text-xs text-slate-500">Sujet</div>
                  <div className="mt-1 text-sm font-medium">
                    {applyPreviewVars(form.subject)}
                  </div>
                </div>
              </div>

              <div className="rounded-3xl overflow-hidden border border-slate-200 dark:border-slate-800 bg-white">
                <div
                  className="max-h-[520px] overflow-auto"
                  dangerouslySetInnerHTML={{ __html: previewHtml }}
                />
              </div>

              <div className="rounded-3xl bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 p-4">
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-950 text-white dark:bg-slate-100 dark:text-slate-950 px-4 py-3 text-sm font-medium disabled:opacity-50"
                >
                  <FaSave />
                  {saving ? "Sauvegarde…" : "Sauvegarder le modèle"}
                </button>

                <button
                  type="button"
                  onClick={onClose}
                  className="mt-2 w-full rounded-2xl bg-slate-100 dark:bg-slate-800 px-4 py-3 text-sm"
                >
                  Annuler
                </button>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <div className="mb-1.5 text-xs font-medium text-slate-600 dark:text-slate-400">
        {label}
      </div>
      {children}
    </label>
  );
}

function BlockEditor({
  block,
  index,
  onChange,
  onRemove,
  onMoveUp,
  onMoveDown,
  canMoveUp,
  canMoveDown,
}) {
  const label =
    BLOCK_TYPES.find((type) => type.value === block.type)?.label || block.type;

  return (
    <div className="rounded-3xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/70 p-4">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-slate-950 text-white dark:bg-slate-100 dark:text-slate-950 text-xs font-semibold">
            {index + 1}
          </span>
          <div className="font-medium">{label}</div>
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            disabled={!canMoveUp}
            onClick={onMoveUp}
            className="rounded-xl bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 px-3 py-2 text-xs disabled:opacity-40"
          >
            <FaArrowUp />
          </button>

          <button
            type="button"
            disabled={!canMoveDown}
            onClick={onMoveDown}
            className="rounded-xl bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 px-3 py-2 text-xs disabled:opacity-40"
          >
            <FaArrowDown />
          </button>

          <button
            type="button"
            onClick={onRemove}
            className="rounded-xl bg-rose-50 text-rose-700 dark:bg-rose-950/40 dark:text-rose-200 px-3 py-2 text-xs"
          >
            <FaTrash />
          </button>
        </div>
      </div>

      <div className="mt-4">
        {(block.type === "title" ||
          block.type === "subtitle" ||
          block.type === "paragraph") && (
          <textarea
            rows={block.type === "paragraph" ? 5 : 2}
            value={block.content || ""}
            onChange={(e) => onChange({ content: e.target.value })}
            className="w-full rounded-2xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 px-4 py-3 text-sm"
          />
        )}

        {block.type === "image" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="URL de l’image">
              <div className="relative">
                <FaImage className="absolute left-3 top-3 text-slate-400" />
                <input
                  value={block.src || ""}
                  onChange={(e) => onChange({ src: e.target.value })}
                  placeholder="https://..."
                  className="w-full rounded-2xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 pl-10 pr-4 py-2.5 text-sm"
                />
              </div>
            </Field>

            <Field label="Texte alternatif">
              <input
                value={block.alt || ""}
                onChange={(e) => onChange({ alt: e.target.value })}
                className="w-full rounded-2xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 px-4 py-2.5 text-sm"
              />
            </Field>
          </div>
        )}

        {block.type === "button" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Texte du bouton">
              <input
                value={block.label || ""}
                onChange={(e) => onChange({ label: e.target.value })}
                className="w-full rounded-2xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 px-4 py-2.5 text-sm"
              />
            </Field>

            <Field label="URL du bouton">
              <div className="relative">
                <FaLink className="absolute left-3 top-3 text-slate-400" />
                <input
                  value={block.url || ""}
                  onChange={(e) => onChange({ url: e.target.value })}
                  placeholder="https://..."
                  className="w-full rounded-2xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 pl-10 pr-4 py-2.5 text-sm"
                />
              </div>
            </Field>
          </div>
        )}

        {block.type === "divider" && (
          <div className="rounded-2xl bg-white dark:bg-slate-950 border border-dashed border-slate-300 dark:border-slate-700 p-4 text-sm text-slate-500">
            Ligne de séparation.
          </div>
        )}

        {block.type === "signature" && (
          <div className="rounded-2xl bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 p-4 text-sm text-slate-600 dark:text-slate-300">
            <div className="inline-flex items-center gap-2 font-medium">
              <FaSignature />
              Signature Laurent VICTORIN
            </div>
            <div className="mt-1 text-xs text-slate-500">
              La signature est générée automatiquement côté serveur.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
